import { useEffect, useState } from "react";
import { $getRoot, createEditor } from "lexical";

import { richTextNodes } from "@/components/rich-text-editor/rich-text-nodes";
import { theme } from "@/components/rich-text-editor/rich-text-theme";
import { cn } from "@/lib/cn";

const richTextCache = new Map<string, string>();

interface DatatableCellRichTextProps {
  richText?: string;
  className?: string;
  maxLines?: number;
}

export const DatatableCellRichText = ({ richText, className, maxLines = 1 }: DatatableCellRichTextProps) => {
  const initialPlainText = richText && richTextCache.has(richText) ? richTextCache.get(richText)! : "";
  const [plainText, setPlainText] = useState(initialPlainText);

  useEffect(() => {
    if (!richText) return;

    if (richTextCache.has(richText)) {
      setPlainText(richTextCache.get(richText)!);
      return;
    }

    const editor = createEditor({
      namespace: "ark-monitoring",
      theme,
      nodes: richTextNodes,
      editable: false,
      onError: (error) => console.error("Lexical error in parser", error),
    });

    try {
      const editorState = editor.parseEditorState(richText);
      editor.setEditorState(editorState);
      editorState.read(() => {
        const root = $getRoot();
        const text = root.getTextContent().replace(/\n+/g, " ").trim();
        richTextCache.set(richText, text);
        setPlainText(text);
      });
    } catch (err) {
      console.error("Failed to parse richText:", err);
    }
  }, [richText]);

  const lineClampClass = maxLines === 1 ? "line-clamp-1" : `line-clamp-[${maxLines}]`;

  return (
    <div
      className={cn(
        "relative flex h-full min-w-[10rem] flex-row items-center justify-start gap-1 truncate text-ellipsis px-2",
        className,
      )}
    >
      <div className={cn("overflow-hidden text-ellipsis pr-2 text-xs", lineClampClass)}>{plainText}</div>
    </div>
  );
};
